import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { BASIC_DARK_GRAY, BASIC_GRAY } from "../../../constants/colors";
import { appActions } from "../../../ducks/app";
import { Menu } from "../../../ducks/menu";
import { Button } from "../../atoms/Button";
import { Select } from "../../atoms/Select";
import { MenuCountSelect } from "../../molecules/menu/MenuCountSelect";
import { MenuImage } from "../../molecules/menu/MenuImage";
import { MenuPrice } from "../../molecules/menu/MenuPrice";

type Props = {
  menu: Menu;
  selectedLimitedMenus: Menu[];
  onFavorite: (
    menuCode: string,
    favorite: boolean,
    onSuccess?: () => void,
  ) => void;
  onSelectMenu: (args: {
    amount: number;
    optionId?: number;
    optionName?: string;
  }) => void;
  noPopup?: boolean;
  disableClick?: boolean;
};

export const MenuItem: React.FC<Props> = ({
  menu,
  selectedLimitedMenus,
  onFavorite,
  onSelectMenu,
  noPopup,
  disableClick,
}) => {
  const dispatch = useDispatch();

  const [isMenuClicked, setIsMenuClicked] = useState<boolean>(false);

  const [count, setCount] = useState(1);
  const [selectedOption, setSelectedOption] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [animate, setAnimate] = useState(false);

  const shouldDisable = useMemo(() => {
    return menu.isPreorder && !menu.isEnableButton;
  }, [menu]);

  const handleAnimation = useCallback(() => {
    setAnimate(true);
    setTimeout(() => setAnimate(false), 1000);
  }, []);

  const handleAddToCart = useCallback(
    (amount: number, optionId?: number, optionName?: string) => {
      if (amount === 0) return;

      if (!isMenuClicked || !menu.isFavorite) {
        handleAnimation();
      } else {
        const animationTrigger = document.getElementById(
          `menu-item-animation-trigger-${menu.id}`,
        );
        if (animationTrigger) animationTrigger.click();
      }

      onSelectMenu({
        amount,
        optionId,
        optionName,
      });
    },
    [isMenuClicked, menu],
  );

  const handleClickMenuItem = useCallback(
    (isClickEvent?: boolean) => {
      if (disableClick) return;

      if (isClickEvent) setIsMenuClicked(true);

      if (noPopup) {
        const menuItem = document.getElementById(`menu-item-${menu.id}`);
        if (menuItem) menuItem.click();
      } else {
        dispatch(
          appActions.showMenuDetailPopup.started({
            menu,
            isLimitedMenuSelected,
            onFavorite: (favorite) => {
              onFavorite(menu.code, favorite, () => {
                dispatch(appActions.setMenuDetailPopupFavorite(favorite));
              });
            },
            onSelectMenu: handleAddToCart,
            onClose: () => dispatch(appActions.closeMenuDetailPopup()),
          }),
        );
      }
    },
    [menu],
  );

  const isLimitedMenuSelected = useMemo(() => {
    if (!menu.isLimitedMenu) return false;

    return selectedLimitedMenus.some(
      (selectedMenu) => selectedMenu.code === menu.code,
    );
  }, [selectedLimitedMenus]);

  return (
    <>
      <div
        id={noPopup ? `menu-item-animation-trigger-${menu.id}` : undefined}
        style={{ display: "none" }}
        onClick={handleAnimation}
      ></div>

      <MenuItemContainer>
        <MenuInformation>
          <AnimatedMenuImage $animate={animate}>
            <MenuImage
              id={menu.id}
              imageUrl={menu.imageUrl}
              isFavorite={menu.isFavorite}
              width={110}
              height={110}
              onFavorite={(favorite) => onFavorite(menu.code, favorite)}
              onClickImage={() => handleClickMenuItem(noPopup)}
            />
          </AnimatedMenuImage>

          <MenuDetailContainer>
            <MenuDetail
              id={noPopup ? undefined : `menu-item-${menu.id}`}
              onClick={() => handleClickMenuItem(true)}
              onMouseDown={() => handleClickMenuItem(false)}
            >
              <MenuName>{menu.name}</MenuName>

              <MenuPrice
                price={menu.price}
                priceWithTax={menu.priceWithTax}
                isOriginalPrice={!!menu.campaign}
              />

              {menu.campaign && (
                <>
                  <MenuPrice
                    price={menu.campaign.price}
                    priceWithTax={menu.campaign.priceWithTax}
                    isDiscountPrice
                  />
                  <MenuCampaignDetail>
                    {menu.campaign.details}
                  </MenuCampaignDetail>
                </>
              )}
            </MenuDetail>

            <MenuOptionSelect>
              {menu.options.length > 0 && (
                <Select
                  name="menuOption"
                  padding="2px 0 2px 5px"
                  placeholder="-"
                  top="31px"
                  width="100%"
                  onChange={(name, id) => setSelectedOption({ id, name })}
                  disabled={shouldDisable}
                  options={menu.options.map((option) => ({
                    id: option.id,
                    name: option.name,
                    value: option.id,
                  }))}
                />
              )}
            </MenuOptionSelect>

            {menu.isPreorder && (
              <MenuPreorderMessage>{menu.preorderMessage}</MenuPreorderMessage>
            )}
          </MenuDetailContainer>
        </MenuInformation>

        <MenuActions>
          <MenuCountSelect
            initialCount={count}
            onChange={setCount}
            disabled={shouldDisable}
            limited={menu.isLimitedMenu}
            limitedDisabled={isLimitedMenuSelected}
            shouldReset={count === 1}
          />

          <ButtonContainer>
            <Button
              size="xs"
              btnStyle={shouldDisable ? "disabled" : "primary"}
              text={shouldDisable ? "予約商品" : "カートへ"}
              disabled={shouldDisable || isLimitedMenuSelected || count === 0}
              onClick={() => {
                handleAddToCart(
                  count,
                  selectedOption?.id,
                  selectedOption?.name,
                );
                setCount(1);
              }}
            />
          </ButtonContainer>
        </MenuActions>
      </MenuItemContainer>
    </>
  );
};

const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 40px 0;
  color: ${BASIC_DARK_GRAY};
`;

const MenuInformation = styled.div`
  display: flex;
  padding-right: 10px;
`;

const MenuDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MenuDetail = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 15px;
  cursor: pointer;
`;

const MenuName = styled.div`
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const MenuOptionSelect = styled.div`
  padding-left: 15px;
  margin: 5px 0;
`;

const MenuCampaignDetail = styled.div`
  text-align: left;
  font-size: 12px;
  color: ${BASIC_DARK_GRAY};
  line-height: 2em;
  word-wrap: break-word;
  white-space: normal;
`;

const MenuPreorderMessage = styled.div`
  font-size: 12px;
  padding-left: 15px;
  color: ${BASIC_GRAY};
`;

const MenuActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  button {
    width: 120px;
  }
`;

const falling = keyframes`
  0% {
    transform: translate(0, 0) scale(1,1) rotate(0deg);
    opacity: 1;
  }
  25% {
    transform: translate(35px, -90px) scale(1,1) rotate(30deg);
    opacity: 1;
  }
  50% {
    transform: translate(100px, 200px) scale(0,0) rotate(180deg);
    opacity: 0;
  }
  75% {
    transform: translate(125px, 200px) scale(0,0) rotate(180deg);
    opacity: 0;
  }
  76% {
    transform: translate(0, 0) scale(0,0) rotate(0deg);
    opacity: 0;
  }
  100% {
    transform: translate(0, 0) scale(0,0) rotate(0deg);
    opacity: 1;
  }
`;

const AnimatedMenuImage = styled(MenuItemContainer)<{ $animate: boolean }>`
  margin-bottom: 20px;
  ${(props) =>
    props.$animate &&
    css`
      animation: ${falling} 1.5s ease-in-out 0s 1 alternate none running;
    `};
`;

const ButtonContainer = styled.div`
  button {
    padding: 12px 38px;
    width: auto;
  }
`;
